/**
 * Choosing a product's category from the ones the clinic already uses.
 *
 * Category was a free-text box, so "Serum", "serum" and "Serums" became three
 * categories without anyone meaning it. This offers the existing ones first
 * and makes adding a new one a deliberate step.
 *
 * ## Categories are not their own table
 *
 * A category is just the text on a product; the list here is "every distinct
 * category on the clinic's products". That is why there is no separate screen
 * to manage them, and why a new category exists from the moment a product is
 * saved with it and disappears when the last product using it changes.
 */
import { useEffect, useState } from 'react';
import { listProducts } from '../lib/api.js';
import { c, sans } from '../theme.js';

const ADD_NEW = '__add_new__';

/** The clinic's categories, alphabetical, case-insensitively de-duplicated. */
export function useCategories(refreshToken: unknown = 0): string[] {
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    let current = true;
    listProducts()
      .then((res) => {
        if (!current) return;
        const seen = new Map<string, string>();
        for (const p of res.products) {
          const name = p.category?.trim();
          if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
        }
        setCategories([...seen.values()].sort((a, b) => a.localeCompare(b)));
      })
      // The list is a convenience; without it the field still lets you type.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [refreshToken]);

  return categories;
}

interface Props {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  categories: string[];
  style?: React.CSSProperties;
  'aria-label'?: string;
}

export function CategoryField({ id, value, onChange, categories, style, ...rest }: Props) {
  // Typing a new one. Also the starting state when the product's category is
  // not in the list (or there is no list yet), so the value is never hidden.
  const known = categories.some((cat) => cat.toLowerCase() === value.trim().toLowerCase());
  const [adding, setAdding] = useState(false);
  const typing = adding || (value.trim() !== '' && !known) || categories.length === 0;

  if (typing) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          id={id}
          value={value}
          autoFocus={adding}
          onChange={(e) => onChange(e.target.value)}
          placeholder={categories.length === 0 ? 'e.g. Dermal filler' : 'New category name'}
          aria-label={rest['aria-label'] ?? 'New category'}
          style={{ ...style, flex: 1, minWidth: 0 }}
        />
        {categories.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              onChange('');
            }}
            style={{
              border: 'none',
              background: 'none',
              padding: '4px 2px',
              fontFamily: sans,
              fontSize: 13,
              color: c.green,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Choose existing
          </button>
        )}
      </div>
    );
  }

  return (
    <select
      id={id}
      value={known ? categories.find((cat) => cat.toLowerCase() === value.trim().toLowerCase()) : ''}
      onChange={(e) => {
        if (e.target.value === ADD_NEW) {
          setAdding(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
      aria-label={rest['aria-label'] ?? 'Category'}
      style={{ ...style, fontFamily: sans }}
    >
      <option value="">No category</option>
      {categories.map((cat) => (
        <option key={cat} value={cat}>
          {cat}
        </option>
      ))}
      <option value={ADD_NEW}>Add a new category…</option>
    </select>
  );
}
